import React, { useEffect, useMemo, useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useLocalStorage } from '@tzmedical/react-hooks';

import axios from '../../axiosClient.js';
import CommissionBarChart from '../CommissionBarChart.jsx';
import RepSelector from '../RepSelector.jsx';

// ────────────────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────────────────
const styles = {
  container: { padding: '20px' },
  controlsRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  filters: { display: 'flex', gap: '10px', alignItems: 'center' },
  gridContainer: { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '15px' },
  gridItem: {
    border: '1px dashed #ccc',
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  addBtn: { fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' },
  removeBtn: {
    type: 'button',
    position: 'absolute',
    top: 5,
    right: 5,
    background: 'none',
    border: 'none',
    fontSize: 16,
    cursor: 'pointer',
    color: '#f44336',
  },
};

// ────────────────────────────────────────────────────────────────────────────────
// LocalStorage keys
// ────────────────────────────────────────────────────────────────────────────────
const LAYOUT_CONFIG_KEY = 'terr-performance-layout'; // << only persist layout (rep/year per slot)
const SELECTED_YEAR_KEY = 'terr-performance-year';
const SELECTED_REP_STORAGE_KEY = 'terr-performance-rep';
const SELECTED_SAVE_KEY = 'terr-performance-selected-save'; // optionally remember which save is selected

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }),
}));

// ────────────────────────────────────────────────────────────────────────────────
// Custom hook: fetch current user and permitted reps
// ────────────────────────────────────────────────────────────────────────────────
function usePermissions() {
  const [user, setUser] = useState(null);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/api/permissions/commissions');
        setUser(data[0]?.username || null);
        setReps([...new Set(data.map((p) => p.rep))]);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { user, reps, loading, error };
}

// ────────────────────────────────────────────────────────────────────────────────
// Utility: compute a common y-axis max across all datasets
// ────────────────────────────────────────────────────────────────────────────────
function computeYMax(allDataArrays) {
  let maxRevenueSum = 0;
  let maxStackSum = 0;

  allDataArrays.forEach((arr) => {
    if (!Array.isArray(arr)) {
      return;
    }

    const revenueSum = arr.reduce((sum, r) => sum + (Number(r.total_net_sales) || 0), 0);
    const stackSum = arr.reduce(
      (sum, r) =>
        sum +
        (Number(r.total_base) || 0) +
        (Number(r.total_expenses) || 0) +
        (Number(r.total_commissions) || 0) +
        (Number(r.total_bonus) || 0),
      0
    );

    maxRevenueSum = Math.max(maxRevenueSum, revenueSum);
    maxStackSum = Math.max(maxStackSum, stackSum);
  });

  return Math.max(maxRevenueSum, maxStackSum) + 20000;
}

// ────────────────────────────────────────────────────────────────────────────────
// Main component: TerrPerformance
// ────────────────────────────────────────────────────────────────────────────────
export default function TerrPerformance() {
  const theme = useTheme();

  // 1) Auth & permissions
  const { user: loginUsername, reps, loading: permsLoading, error: permsError } = usePermissions();

  // 2) Persistent variables only: rep, year, selectedSave, and layoutConfig (NO chart data)
  const defaultRep = reps[0] || 'all-reps';
  const [rep, setRep] = useLocalStorage(SELECTED_REP_STORAGE_KEY, defaultRep);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setYear] = useLocalStorage(SELECTED_YEAR_KEY, currentYear);

  // 3) Persist ONLY the layout structure: [{ rep, year }]
  const [layoutConfig, setLayoutConfig] = useLocalStorage(LAYOUT_CONFIG_KEY, []);

  // Optionally persist the currently selected save id/headerid
  const [selectedSave, setSelectedSave] = useLocalStorage(SELECTED_SAVE_KEY, '');

  const hasCurrentYearChart = useMemo(
    () => layoutConfig.some((slot) => Number(slot?.year) === currentYear),
    [layoutConfig, currentYear]
  );

  const defaultYtdCutoffMonth = hasCurrentYearChart ? new Date().getMonth() + 1 : 12;
  const [selectedCutoffMonth, setSelectedCutoffMonth] = useState(defaultYtdCutoffMonth);
  const [isCutoffMonthManual, setIsCutoffMonthManual] = useState(false);

  useEffect(() => {
    if (!isCutoffMonthManual) {
      setSelectedCutoffMonth(defaultYtdCutoffMonth);
    }
  }, [defaultYtdCutoffMonth, isCutoffMonthManual]);

  const ytdCutoffMonth = selectedCutoffMonth;
  const ytdCutoffMonthLabel = useMemo(
    () => new Date(2000, ytdCutoffMonth - 1, 1).toLocaleString('en-US', { month: 'long' }),
    [ytdCutoffMonth]
  );

  // 4) In-memory: actual chart slots (with data) and dialogs
  const [charts, setCharts] = useState([null]); // not persisted
  const [saves, setSaves] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogName, setDialogName] = useState('');

  // 5) Shared y-axis max (based on in-memory data)
  const yMax = useMemo(() => {
    const dataArrays = charts.filter((c) => c?.status === 'success').map((c) => c.data);
    return computeYMax(dataArrays);
  }, [charts]);

  // Keep selected rep valid when rep options refresh
  useEffect(() => {
    if (!Array.isArray(reps) || reps.length === 0) {
      return;
    }

    const current = String(rep ?? '')
      .trim()
      .toUpperCase();
    const hasCurrent = reps.some(
      (r) =>
        String(r ?? '')
          .trim()
          .toUpperCase() === current
    );
    if (!hasCurrent) {
      setRep(reps[0]);
    }
  }, [reps, rep, setRep]);

  // 6) Load saved-layout list once
  useEffect(() => {
    if (!loginUsername) {
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const { data } = await axios.get(
          `/api/persistedsitedata?appname=terr-performance&username=${encodeURIComponent(loginUsername)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSaves(data);
      } catch {
        setSaves([]);
      }
    })();
  }, [loginUsername]);

  // 6) Whenever layoutConfig changes (including initial mount), rebuild charts by fetching fresh data
  useEffect(() => {
    let cancelled = false;

    async function buildChartsFromLayout() {
      if (!layoutConfig || layoutConfig.length === 0) {
        setCharts([null]);
        return;
      }

      // Show a loading skeleton per slot
      const loadingSlots = layoutConfig.map(({ rep: slotRep, year }) => ({
        id: crypto.randomUUID(),
        rep: slotRep,
        year,
        status: 'loading',
      }));
      setCharts([...loadingSlots, null]);

      // Fetch all fresh
      const token = localStorage.getItem('authToken');
      const fetched = await Promise.all(
        layoutConfig.map(async ({ rep: slotRep, year }) => {
          try {
            const resp = await axios.get(
              `/api/terr-performance/${encodeURIComponent(slotRep)}/${year}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  // Optional: cache-busting if you suspect intermediaries cache
                  'Cache-Control': 'no-cache',
                },
                params: {
                  ts: Date.now(),
                  normalizeToCurrentYtd: hasCurrentYearChart,
                  cutoffMonth: ytdCutoffMonth,
                },
              }
            );
            const rows = resp.data?.data || [];

            // ✅ pull goalRatio from API payload (exact key name depends on what you return)
            const rawGoalRatio = resp.data?.goalRatio ?? resp.data?.meta?.goalRatio ?? null;

            // Parse DECIMAL-as-string ("0.58641") into a real JS number
            const goalRatio = rawGoalRatio == null ? 0 : Number(rawGoalRatio);

            return {
              id: crypto.randomUUID(),
              rep: slotRep,
              year,
              status: 'success',
              data: rows,
              goalRatio, // ✅ store on the slot
            };
          } catch (err) {
            return {
              id: crypto.randomUUID(),
              rep: slotRep,
              year,
              status: 'error',
              error: err?.message || 'Fetch failed',
            };
          }
        })
      );

      if (!cancelled) {
        setCharts([...fetched, null]);
      }
    }

    buildChartsFromLayout();
    return () => {
      cancelled = true;
    };
  }, [layoutConfig, hasCurrentYearChart, ytdCutoffMonth]);

  // Early returns
  if (permsLoading) {
    return <p>Loading permissions…</p>;
  }
  if (permsError || !loginUsername) {
    return <p>Error: not authenticated.</p>;
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────────────────────────────────────

  // rep selector
  const handleRepChange = (newRep) => setRep(newRep);

  // year selector
  const handleYearChange = (e) => setYear(e.target.value);

  // month selector
  const handleCutoffMonthChange = (e) => {
    setIsCutoffMonthManual(true);
    setSelectedCutoffMonth(Number(e.target.value));
  };

  // add new chart slot -> update layoutConfig ONLY
  const handleAddChart = () => {
    setLayoutConfig((prev) => [...prev, { rep, year: selectedYear }]);
  };

  // remove chart slot -> remove from layoutConfig
  const handleRemoveChart = (idx) => {
    // idx corresponds to charts index; translate to layout index (exclude trailing null)
    setLayoutConfig((prev) => {
      const layout = [...prev];
      layout.splice(idx, 1);
      return layout;
    });
  };

  // load saved layout (from backend) -> set layoutConfig ONLY (effect will fetch)
  const handleLoadSave = async (e) => {
    const headerId = e.target.value;
    setSelectedSave(headerId);

    if (!headerId) {
      setLayoutConfig([]); // clears grid
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`/api/persistedsitedata/${headerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const configList = Array.isArray(res.data?.config) ? res.data.config : [];
      const minimalLayout = configList.map(({ rep: slotRep, year }) => ({
        rep: slotRep,
        year,
      }));

      setLayoutConfig(minimalLayout);
    } catch {
      setLayoutConfig([]);
    }
  };

  // dialog open/close
  const openSaveDialog = () => {
    setDialogName('');
    setSaveDialogOpen(true);
  };
  const closeSaveDialog = () => {
    setSaveDialogOpen(false);
    setDialogName('');
  };
  const openDeleteDialog = () => setDeleteDialogOpen(true);
  const closeDeleteDialog = () => setDeleteDialogOpen(false);

  // create or update layout on Save (send minimal layout only)
  const handleDialogConfirm = async () => {
    const token = localStorage.getItem('authToken');
    const minimalConfig = layoutConfig.map(({ rep: slotRep, year }) => ({ rep: slotRep, year }));

    try {
      const existing = saves.find((s) => s.savename === dialogName);
      if (existing) {
        await axios.put(
          `/api/persistedsitedata/${existing.headerid}`,
          { savename: dialogName, config: minimalConfig },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedSave(existing.headerid);
      } else {
        await axios.post(
          '/api/persistedsitedata',
          {
            appname: 'terr-performance',
            username: loginUsername,
            savename: dialogName,
            config: minimalConfig,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // reload list
      const listRes = await axios.get(
        `/api/persistedsitedata?appname=terr-performance&username=${encodeURIComponent(loginUsername)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaves(listRes.data);

      // select newly created
      const just = listRes.data.find((s) => s.savename === dialogName);
      if (just) {
        setSelectedSave(just.headerid);
      }
    } catch {
      // silent
    } finally {
      closeSaveDialog();
    }
  };

  // delete selected layout
  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem('authToken');
    try {
      await axios.delete(`/api/persistedsitedata/${selectedSave}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaves((prev) => prev.filter((s) => s.headerid !== selectedSave));
      setSelectedSave('');
      setLayoutConfig([]);
    } catch {
      // silent
    } finally {
      closeDeleteDialog();
    }
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Filters & Layout Controls */}
      <div style={styles.controlsRow}>
        <div style={styles.filters}>
          <RepSelector onChange={handleRepChange} selectedRep={rep} />

          <FormControl>
            <InputLabel id="year-label">Year</InputLabel>
            <Select
              labelId="year-label"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
              sx={{ minWidth: 120 }}
            >
              {[0, 1, 2].map((o) => (
                <MenuItem key={o} value={currentYear - o}>
                  {currentYear - o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel id="month-label">Month</InputLabel>
            <Select
              labelId="month-label"
              value={ytdCutoffMonth}
              label="Month"
              onChange={handleCutoffMonthChange}
              sx={{ minWidth: 140 }}
            >
              {MONTH_OPTIONS.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel id="layout-label">Layout</InputLabel>
            <Select
              labelId="layout-label"
              value={selectedSave}
              label="Layout"
              onChange={handleLoadSave}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {saves.map(({ headerid, savename }) => (
                <MenuItem key={headerid} value={headerid}>
                  {savename}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={openSaveDialog} disabled={layoutConfig.length === 0}>
            Save
          </Button>

          {selectedSave && (
            <Button variant="outlined" color="error" onClick={openDeleteDialog} sx={{ ml: 1 }}>
              Delete
            </Button>
          )}
        </div>
      </div>

      {layoutConfig.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
          Showing YTD through {ytdCutoffMonthLabel}
        </Typography>
      )}

      {/* Save / Update Dialog */}
      <Dialog open={saveDialogOpen} onClose={closeSaveDialog}>
        <DialogTitle>{selectedSave ? 'Update Layout' : 'Save New Layout'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Layout Name"
            fullWidth
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSaveDialog}>Cancel</Button>
          <Button onClick={handleDialogConfirm} disabled={!dialogName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete this layout?</DialogTitle>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chart Grid */}
      <div style={styles.gridContainer}>
        {charts.map((slot, idx) => (
          <div key={slot?.id ?? idx} style={styles.gridItem}>
            {slot ? (
              <>
                {slot.status === 'loading' && <p>Loading…</p>}
                {slot.status === 'error' && <p>Error: {slot.error}</p>}
                {slot.status === 'success' && (
                  <CommissionBarChart
                    rep={slot.rep}
                    year={slot.year}
                    data={slot.data}
                    goalRatio={slot.goalRatio}
                    yMax={yMax}
                    compact
                  />
                )}
                <button
                  type="button"
                  style={styles.removeBtn}
                  onClick={() => handleRemoveChart(idx)}
                >
                  ✖
                </button>
              </>
            ) : (
              <button
                type="button"
                style={{
                  ...styles.addBtn,
                  color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                }}
                onClick={handleAddChart}
              >
                ＋
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
